import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import {
  GlobalTenantContractsApi,
  TenantContractModel,
  TenantContractsApi,
  FieldMetadata,
  FindContractsQuery,
  FilterMode,
  FilterType,
  SortDirection,
  MetadataModel,
} from 'src/app/usage-api';
import { BaseDataSource, TableColumn } from './datasource';
import { AuthService } from '../../authentication/auth.service';
import { MetadataFilterManager } from './metadata-filter.service';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FindContractsRequest {
  page?: number;
  pageSize?: number;
  query?: FindContractsQuery;
}

const ERROR_MESSAGE_KEY = 'BackOffice.ManageContracts.Table.ErrorMessage';

export class ContractsDataSource extends BaseDataSource<
  TenantContractModel,
  GlobalTenantContractsApi,
  FindContractsRequest
> {
  protected columns: TableColumn<TenantContractModel>[] = [];
  private readonly metadataManager: MetadataFilterManager<TenantContractModel>;
  private readonly defaultTenantId: string;

  // Expose metadata manager observables
  public readonly metadata$: BehaviorSubject<MetadataModel | null>;
  public readonly isMetadataLoaded$: Observable<boolean>;

  get availableColumns(): TableColumn<TenantContractModel>[] {
    return this.metadataManager.getColumns().filter((f) => this.authService.hasAllPermissions(f.permissions ?? []));
  }

  get queryArguments(): object {
    const sortOrder = this.currentRequest.value.query?.sort?.[0]?.order === 'Ascending' ? '' : '-';

    return {
      page: this.currentRequest.value.page,
      pageSize: this.currentRequest.value.pageSize,
      sort: sortOrder + (this.currentRequest.value.query?.sort?.[0]?.field ?? ''),
      isActive: this.filters.isActive$.value !== undefined ? this.filters.isActive$.value : undefined,
      ...this.metadataManager.queryFriendlyFilters(this.currentRequest.value.query?.filters),
    };
  }

  filters = {
    isActive$: new BehaviorSubject<boolean | undefined>(undefined),
  };

  constructor(
    protected service: GlobalTenantContractsApi,
    protected tenantService: TenantContractsApi,
    protected authService: AuthService,
    paginator: MatPaginator,
    sort: MatSort,
    request: FindContractsRequest,
    loadOnInit: boolean = true,
  ) {
    super(service, paginator, sort, request, loadOnInit);
    this.defaultTenantId = authService.user?.snapshot?.tenantId?.toString() ?? '';

    this.metadataManager = new MetadataFilterManager<TenantContractModel>({
      loadMetadata: () => this.tenantService.tenantContractsGetMetadata(this.defaultTenantId),
      getLocalizationKey: this.getLocalizationKey.bind(this),
      customizeColumn: this.customizeColumn.bind(this),
      dateRangeFields: ['contractStartDate', 'servicePeriodEndDate'],
    });

    this.metadata$ = this.metadataManager.metadata$;
    this.isMetadataLoaded$ = this.metadataManager.isMetadataLoaded$;
    this.loadingSubject.next(true);
    this.metadataManager.loadMetadata({
      subscription: (sub) => this.subscriptions.add(sub),
      next: () => {
        // Trigger data load after metadata is ready if init has been called
        if (this.hasInit) {
          this.loadDataTerm$.next(true);
        }
      },
      error: (_) => {
        this.hasError.next(true);
        this.errorMessage.next(ERROR_MESSAGE_KEY);
        this.loadingSubject.next(false);
      },
    });
    this.subscriptions.add(
      this.metadataManager.columnsChanged$.subscribe(() => {
        this.columnsChanged.next(true);
      }),
    );
  }

  override init(setInit: boolean = false) {
    super.init(setInit);

    this.subscriptions.add(this.createSubscription(this.filters.isActive$, true, true));
    this.subscriptions.add(this.createSubscription(this.metadataManager.searchFilters$));

    this.hasInit = true;

    // If metadata is already loaded, trigger data loading
    if (this.metadataManager.isMetadataLoaded$.value) {
      this.loadDataTerm$.next(true);
    }
  }

  public updateFilter(
    field: string,
    values: string[] | null,
    mode: FilterMode = FilterMode.Includes,
    type: FilterType = FilterType.Values,
  ): void {
    this.metadataManager.updateFilter(field, values, mode, type, (sub) => this.subscriptions.add(sub));
  }

  public loadData(forceRefresh: boolean): void {
    if (!this.metadata$?.value?.fields?.length) {
      if (!this.metadataManager.isMetadataLoaded$.value && !this.metadataManager.isLoading$.value) {
        this.loadingSubject.next(true);
        this.hasError.next(false);
        this.errorMessage.next(null);
        this.metadataManager.loadMetadata({
          subscription: (sub) => this.subscriptions.add(sub),
          next: (_) => this.loadData(true),
          error: (_) => {
            this.hasError.next(true);
            this.errorMessage.next(ERROR_MESSAGE_KEY);
            this.loadingSubject.next(false);
          },
        });
      }
      return;
    }
    this.loadDataFromConfig({
      forceRefresh: forceRefresh,
      errorMessage: ERROR_MESSAGE_KEY,
      requestTransformer: () => {
        const request: FindContractsRequest & { active?: boolean } = {
          page: this.pageIndex + 1,
          pageSize: this.pageSize,
          query: {
            sort: this.sortField
              ? [
                  {
                    field: this.sortField,
                    order: (this.sort.direction === 'desc' ? 'Descending' : 'Ascending') as unknown as SortDirection,
                  },
                ]
              : null,
            filters: this.metadataManager.getSearchFilters(),
          },
          active: this.filters.isActive$.value ?? undefined,
        };

        return request;
      },

      request: (request: FindContractsRequest & { active?: boolean }) =>
        this.service.globalTenantContractsFilterContracts(request.active, request.page, request.pageSize, request.query),

      isEqual: (previous: FindContractsRequest & { active?: boolean }, current: FindContractsRequest & { active?: boolean }) =>
        previous.page === current.page &&
        previous.pageSize === current.pageSize &&
        previous.query?.sort?.[0]?.field === current.query?.sort?.[0]?.field &&
        previous.query?.sort?.[0]?.order === current.query?.sort?.[0]?.order &&
        previous.active === current.active &&
        this.metadataManager.areFiltersEqual(previous.query?.filters, current.query?.filters),
    });
  }

  destroy(): void {
    this.metadataManager.destroy();
  }

  private getLocalizationKey(field: FieldMetadata): string {
    const fieldKey = field.key || '';

    const localizationMap: Record<string, string> = {
      tenantName: 'BackOffice.ManageContracts.Contract.TenantName',
      tenantId: 'BackOffice.ManageContracts.Contract.TenantId',
      brand: 'BackOffice.ManageContracts.Contract.Brand',
      contractStartDate: 'BackOffice.ManageContracts.Contract.ContractStartDate',
      servicePeriodEndDate: 'BackOffice.ManageContracts.Contract.ServiceEndDate',
      isActive: 'BackOffice.ManageContracts.Contract.Active',
    };

    return localizationMap[fieldKey] || field.displayName || fieldKey;
  }

  private customizeColumn(column: TableColumn<TenantContractModel>, field: FieldMetadata): void {
    const fieldKey = field.key || '';

    if (fieldKey === 'tenantName') {
      column.link = (val: TenantContractModel) => {
        return {
          route: ['/back-office/contracts', val.tenantId, val.id],
          queryParams: null,
        };
      };
      column.sticky = true;
    }

    if (fieldKey === 'isActive') {
      column.render = (val: TenantContractModel) => {
        return val.isActive ? 'Active' : 'Inactive';
      };
    }
  }
}
